import { useEffect, useRef } from 'react'

import { isAnalyticsExcludedPath } from '@/components/molecules/shared/mixpanel-guards'
import { isAnalyticsActive } from '@/features/analytics/client/analytics-mode'
import { useBeforeNavigate } from '@/lib/navigation/before-navigate'
import { useRouter } from '@/lib/navigation/router'

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>
  }
}

interface GTMProviderProps {
  children: React.ReactNode
}

function pathnameFromRouteUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url.split(/[?#]/, 1)[0] || '/'
  }
}

/**
 * The two calls that leave the single-page app. They live on an object so the
 * jsdom suite can observe them: jsdom makes `window.location` non-configurable
 * and `location.assign`/`replace` read-only own properties, so none can be
 * replaced or spied on. The pre-shim code got the same seam by taking
 * `hardNavigate` as a parameter; that injection point disappeared with the
 * handler factory.
 *
 * `hardReplace` keeps a client `router.replace` a replace: the sign-in exit is
 * `router.replace('/')`, and a push there would leave /sign-in behind in
 * history, where Back bounces straight forward again.
 */
export const navigation = {
  hardNavigate(url: string) {
    window.location.assign(url)
  },
  hardReplace(url: string) {
    window.location.replace(url)
  },
}

/**
 * The document loaded on a tracked route carries the GTM snippet (see the
 * app's HTML head); the document loaded on an excluded route does not. The
 * head snippet is the ONLY place GTM is injected, so a document's analytics
 * realm is fixed by the path it LOADED on, and any client-side transition that
 * crosses the excluded boundary becomes a full document navigation:
 *
 * - tracked → excluded, so the snippet never observes a credential realm;
 * - excluded → tracked, so the first tracked page after sign-in or email
 *   verification has GTM and Mixpanel at all (design 2026-09-22 §5.1, M1 —
 *   every email signup used to walk the wizard with no container).
 *
 * Excluded → excluded and tracked → tracked stay client-side.
 */
export function requiresHardNavigation(nextUrl: string, currentPathname: string): boolean {
  return isAnalyticsExcludedPath(pathnameFromRouteUrl(nextUrl)) !== isAnalyticsExcludedPath(currentPathname)
}

export default function GTMProvider({ children }: GTMProviderProps) {
  const router = useRouter()
  const previousPathRef = useRef(router.pathname)

  useBeforeNavigate((url, action) => {
    if (!isAnalyticsActive()) return 'allow'
    if (!requiresHardNavigation(url, window.location.pathname)) return 'allow'
    if (action === 'replace') navigation.hardReplace(url)
    else navigation.hardNavigate(url)
    return 'block'
  })

  // Back/Forward never reaches the blocker (see before-navigate.ts), so the
  // same crossing rule is applied on arrival. The history entry already holds
  // the destination, so the reload replaces it rather than stacking a
  // duplicate entry and dropping Forward history.
  useEffect(() => {
    const previousPath = previousPathRef.current
    previousPathRef.current = router.pathname

    if (!isAnalyticsActive()) return
    const excludedNow = isAnalyticsExcludedPath(router.pathname)
    if (excludedNow !== isAnalyticsExcludedPath(previousPath)) {
      navigation.hardReplace(router.asPath || router.pathname)
      return
    }
    if (!excludedNow) window.dataLayer = window.dataLayer || []
  }, [router.asPath, router.pathname])

  return <>{children}</>
}
